import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's add-to-cart button
 * @property {HTMLButtonElement} [buyNowButton] - Sticky bar's buy-now button (only present when variant is available)
 * @property {HTMLInputElement} quantityInput - Quantity number input
 * @property {HTMLButtonElement} quantityDecreaseButton - Decrease quantity button
 * @property {HTMLButtonElement} quantityIncreaseButton - Increase quantity button
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityInput'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    // Show the sticky bar right away instead of waiting for the user to
    // scroll past the main buy buttons. It will still hide near the page
    // footer so it doesn't cover it (see #mainBottomObserver below).
    this.#showStickyBar();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Kept for future use (e.g. re-showing after the footer observer hides
    // the bar) but no longer used to hide the bar while buy buttons are
    // visible — the sticky bar is shown immediately on load instead.
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && this.#hiddenByBottom) {
        // Buy buttons scrolled out of view again after being hidden by the
        // footer observer (e.g. user scrolled back up) - show the bar again.
        this.#hiddenByBottom = false;
        this.#showStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // Make sure the main product form's quantity matches what was set in the sticky bar
    this.#syncMainQuantityInput();

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles the decrease-quantity button click in the sticky bar
   */
  handleQuantityDecrease = () => {
    this.#changeQuantity(-1);
  };

  /**
   * Handles the increase-quantity button click in the sticky bar
   */
  handleQuantityIncrease = () => {
    this.#changeQuantity(1);
  };

  /**
   * Handles direct typing into the quantity input
   */
  handleQuantityInputChange = () => {
    const { quantityInput } = this.refs;
    const min = parseInt(quantityInput.min, 10) || 1;
    const max = quantityInput.max ? parseInt(quantityInput.max, 10) : Infinity;

    let value = parseInt(quantityInput.value, 10);
    if (isNaN(value)) value = min;
    value = Math.max(min, Math.min(max, value));

    this.#currentQuantity = value;
    quantityInput.value = value.toString();

    // Update disabled state on the decrease button right away.
    this.#updateButtonText();

    this.#syncMainQuantityInput();
  };

  /**
   * Handles the buy-now button click in the sticky bar.
   * Dynamic checkout buttons (Shop Pay / Apple Pay / etc.) render inside an
   * iframe and can't be proxy-clicked, so this adds the item to the cart
   * directly via the Cart AJAX API and redirects to checkout.
   */
  handleBuyNowClick = async () => {
    const variantId = this.dataset.currentVariantId;
    if (!variantId) return;

    const { buyNowButton } = this.refs;
    buyNowButton?.setAttribute('aria-busy', 'true');
    if (buyNowButton) buyNowButton.disabled = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: this.#currentQuantity || 1,
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.description || 'Failed to add item to cart');
      }

      window.location.href = `${window.Shopify?.routes?.root ?? '/'}checkout`;
    } catch (error) {
      console.error('Buy now failed:', error);
      buyNowButton?.removeAttribute('aria-busy');
      if (buyNowButton) buyNowButton.disabled = false;
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events coming from the MAIN page's
   * quantity selector (one-way sync into the sticky bar).
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Changes the current quantity by a delta (used by +/- buttons), clamped
   * to the min/max/step defined on the quantity input, then syncs the change
   * back to the main product form's quantity selector.
   * @param {number} direction - -1 to decrease, 1 to increase
   */
  #changeQuantity(direction) {
    const { quantityInput } = this.refs;
    const min = parseInt(quantityInput.min, 10) || 1;
    const max = quantityInput.max ? parseInt(quantityInput.max, 10) : Infinity;
    const step = parseInt(quantityInput.step, 10) || 1;

    let newQuantity = this.#currentQuantity + direction * step;
    newQuantity = Math.max(min, Math.min(max, newQuantity));

    this.#currentQuantity = newQuantity;
    quantityInput.value = newQuantity.toString();

    // Update disabled state on the decrease button right away, instead of
    // waiting for a round-trip through the main page's quantity selector.
    this.#updateButtonText();

    this.#syncMainQuantityInput();
  }

  /**
   * Pushes the sticky bar's current quantity onto the main product form's
   * quantity input (if present) so that Add to Cart / Buy Now use the right
   * amount. Dispatches a native 'change' event so any listeners on the main
   * quantity selector (including this component's own main->sticky sync)
   * pick up the new value.
   */
  #syncMainQuantityInput() {
    const productForm = this.#getProductForm();
    const mainQuantityInput = productForm?.querySelector('input[name="quantity"]');
    if (!mainQuantityInput || !(mainQuantityInput instanceof HTMLInputElement)) return;
    if (parseInt(mainQuantityInput.value, 10) === this.#currentQuantity) return;

    mainQuantityInput.value = this.#currentQuantity.toString();
    mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Keeps the quantity input in sync with internal state and enables/disables
   * the decrease button at the minimum bound.
   */
  #updateButtonText() {
    const { quantityInput, quantityDecreaseButton } = this.refs;

    quantityInput.value = this.#currentQuantity.toString();

    const min = parseInt(quantityInput.min, 10) || 1;
    if (quantityDecreaseButton) {
      quantityDecreaseButton.disabled = this.#currentQuantity <= min;
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}